import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
  X,
  Copy,
  Check,
  QrCode,
  Link,
  Mail,
  RefreshCw,
  Download,
  Share2,
  Key,
  Calendar,
  Lock,
  Unlock,
  UserCheck,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ShareGalleryModal({ galleryId, galleryName, isOpen, onClose, language = 'pl' }) {
  const [loading, setLoading] = useState(true);
  const [shareInfo, setShareInfo] = useState(null);
  const [copied, setCopied] = useState(null);
  const [regenerating, setRegenerating] = useState(false);
  const [clientQr, setClientQr] = useState(null);
  const [generatingClientQr, setGeneratingClientQr] = useState(false);
  const [activeTab, setActiveTab] = useState('standard'); // 'standard' or 'client'

  useEffect(() => {
    if (isOpen && galleryId) {
      fetchShareInfo();
    }
  }, [isOpen, galleryId]);

  const fetchShareInfo = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/galleries/${galleryId}/share-info`);
      setShareInfo(response.data);
    } catch (error) {
      toast.error(language === 'pl' ? 'Błąd pobierania danych' : 'Error fetching data');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(type);
      toast.success(language === 'pl' ? 'Skopiowano!' : 'Copied!');
      setTimeout(() => setCopied(null), 2000);
    } catch (error) {
      toast.error(language === 'pl' ? 'Błąd kopiowania' : 'Copy failed');
    }
  };

  const handleRegenerateCode = async () => {
    setRegenerating(true);
    try {
      const response = await axios.post(`${API}/galleries/${galleryId}/regenerate-code`);
      setShareInfo(prev => ({ ...prev, access_code: response.data.access_code }));
      toast.success(language === 'pl' ? 'Kod odświeżony' : 'Code regenerated');
    } catch (error) {
      toast.error(language === 'pl' ? 'Błąd' : 'Error');
    } finally {
      setRegenerating(false);
    }
  };

  const handleDownloadQR = () => {
    if (!shareInfo?.qr_base64) return;
    const link = document.createElement('a');
    link.href = shareInfo.qr_base64;
    link.download = `qr-${shareInfo?.access_code || 'gallery'}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleEmailShare = () => {
    const subject = encodeURIComponent(language === 'pl' ? `Twoja galeria zdjęć: ${galleryName}` : `Your photo gallery: ${galleryName}`);
    const body = encodeURIComponent(
      language === 'pl'
        ? `Cześć!\n\nTwoja galeria zdjęć jest gotowa do przeglądania.\n\nLink do galerii: ${shareInfo?.gallery_url}\n\nKod dostępu: ${shareInfo?.access_code}\n\nMożesz też użyć krótkiego linku: ${window.location.origin}/c/${shareInfo?.access_code}\n\nPozdrawiam!`
        : `Hello!\n\nYour photo gallery is ready to view.\n\nGallery link: ${shareInfo?.gallery_url}\n\nAccess code: ${shareInfo?.access_code}\n\nYou can also use short link: ${window.location.origin}/c/${shareInfo?.access_code}\n\nBest regards!`
    );
    window.open(`mailto:?subject=${subject}&body=${body}`);
  };

  const handleGenerateClientQr = async () => {
    setGeneratingClientQr(true);
    try {
      const response = await axios.post(`${API}/galleries/${galleryId}/generate-client-token`);
      setClientQr(response.data);
      toast.success(language === 'pl' ? 'QR dla klienta wygenerowany' : 'Client QR generated');
    } catch (error) {
      toast.error(language === 'pl' ? 'Błąd generowania QR' : 'Error generating QR');
    } finally {
      setGeneratingClientQr(false);
    }
  };

  const handleDownloadClientQR = () => {
    if (!clientQr?.qr_base64) return;
    const link = document.createElement('a');
    link.href = clientQr.qr_base64;
    link.download = `qr-klient-${galleryName || 'gallery'}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleRevokeClientToken = async () => {
    try {
      await axios.post(`${API}/galleries/${galleryId}/revoke-client-token`);
      setClientQr(null);
      toast.success(language === 'pl' ? 'Token unieważniony' : 'Token revoked');
    } catch (error) {
      toast.error(language === 'pl' ? 'Błąd' : 'Error');
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-card rounded-sm max-w-lg w-full max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-border">
            <div className="flex items-center gap-3">
              <Share2 className="w-5 h-5 text-primary" />
              <h2 className="text-lg font-semibold">
                {language === 'pl' ? 'Udostępnij galerię' : 'Share Gallery'}
              </h2>
            </div>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="w-5 h-5" />
            </Button>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-border">
            <button
              onClick={() => setActiveTab('standard')}
              className={`flex-1 px-4 py-3 text-sm font-medium flex items-center justify-center gap-2 ${
                activeTab === 'standard'
                  ? 'text-primary border-b-2 border-primary'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Key className="w-4 h-4" />
              {language === 'pl' ? 'Standardowy' : 'Standard'}
            </button>
            <button
              onClick={() => setActiveTab('client')}
              className={`flex-1 px-4 py-3 text-sm font-medium flex items-center justify-center gap-2 ${
                activeTab === 'client'
                  ? 'text-primary border-b-2 border-primary'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Unlock className="w-4 h-4" />
              {language === 'pl' ? 'QR dla klienta' : 'Client QR'}
              {shareInfo?.has_password && (
                <span className="text-xs bg-green-500/20 text-green-500 px-1.5 py-0.5 rounded">
                  {language === 'pl' ? 'bez hasła' : 'no password'}
                </span>
              )}
            </button>
          </div>

          {loading ? (
            <div className="p-6 flex justify-center">
              <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : shareInfo && activeTab === 'standard' && (
            <div className="p-6 space-y-6">
              {/* QR Code */}
              <div className="text-center">
                <div className="bg-white p-4 rounded-sm inline-block mb-4">
                  <img
                    src={shareInfo.qr_base64}
                    alt="QR Code"
                    className="w-48 h-48"
                  />
                </div>
                <Button variant="outline" size="sm" onClick={handleDownloadQR}>
                  <Download className="w-4 h-4 mr-2" />
                  {language === 'pl' ? 'Pobierz QR' : 'Download QR'}
                </Button>
              </div>

              {/* Access Code */}
              <div className="p-4 bg-secondary rounded-sm">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Key className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">
                      {language === 'pl' ? 'Kod dostępu' : 'Access Code'}
                    </span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleRegenerateCode}
                    disabled={regenerating}
                  >
                    <RefreshCw className={`w-4 h-4 ${regenerating ? 'animate-spin' : ''}`} />
                  </Button>
                </div>
                <div className="flex items-center gap-2">
                  <code className="text-2xl font-bold tracking-widest flex-1">
                    {shareInfo.access_code}
                  </code>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleCopy(shareInfo.access_code, 'code')}
                  >
                    {copied === 'code' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>

              {/* Links */}
              <div className="space-y-3">
                {/* Full Link */}
                <div>
                  <label className="text-sm text-muted-foreground flex items-center gap-2 mb-1">
                    <Link className="w-4 h-4" />
                    {language === 'pl' ? 'Link do galerii' : 'Gallery Link'}
                  </label>
                  <div className="flex gap-2">
                    <Input
                      value={shareInfo.gallery_url}
                      readOnly
                      className="text-sm"
                    />
                    <Button
                      variant="outline"
                      onClick={() => handleCopy(shareInfo.gallery_url, 'url')}
                    >
                      {copied === 'url' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>

                {/* Short Link */}
                <div>
                  <label className="text-sm text-muted-foreground flex items-center gap-2 mb-1">
                    <QrCode className="w-4 h-4" />
                    {language === 'pl' ? 'Krótki link (z kodem)' : 'Short Link (with code)'}
                  </label>
                  <div className="flex gap-2">
                    <Input
                      value={`${window.location.origin}/c/${shareInfo.access_code}`}
                      readOnly
                      className="text-sm"
                    />
                    <Button
                      variant="outline"
                      onClick={() => handleCopy(`${window.location.origin}/c/${shareInfo.access_code}`, 'short')}
                    >
                      {copied === 'short' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
              </div>

              {/* Info */}
              <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                {shareInfo.has_password && (
                  <div className="flex items-center gap-1">
                    <Lock className="w-4 h-4" />
                    {language === 'pl' ? 'Chronione hasłem' : 'Password protected'}
                  </div>
                )}
                {shareInfo.expires_at && (
                  <div className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    {language === 'pl' ? 'Wygasa' : 'Expires'}: {new Date(shareInfo.expires_at).toLocaleDateString()}
                  </div>
                )}
              </div>

              {/* Email Button */}
              <Button className="w-full" onClick={handleEmailShare}>
                <Mail className="w-4 h-4 mr-2" />
                {language === 'pl' ? 'Wyślij zaproszenie email' : 'Send email invitation'}
              </Button>
            </div>
          )}

          {/* Client QR Tab */}
          {shareInfo && activeTab === 'client' && (
            <div className="p-6 space-y-6">
              {/* Info banner */}
              <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-sm">
                <div className="flex items-start gap-3">
                  <UserCheck className="w-5 h-5 text-green-500 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-green-500">
                      {language === 'pl' ? 'QR bez hasła' : 'Password-free QR'}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {language === 'pl'
                        ? 'Klient skanuje ten kod i wchodzi do galerii bez podawania hasła. Idealne do wydruku lub wysłania klientowi.'
                        : 'Client scans this code and enters the gallery without a password. Perfect for printing or sending to clients.'}
                    </p>
                  </div>
                </div>
              </div>

              {clientQr ? (
                <>
                  {/* Client QR Code */}
                  <div className="text-center">
                    <div className="bg-white p-4 rounded-sm inline-block mb-4 relative">
                      <img
                        src={clientQr.qr_base64}
                        alt="Client QR Code"
                        className="w-48 h-48"
                      />
                      <div className="absolute -top-2 -right-2 bg-green-500 text-white text-xs px-2 py-1 rounded-full">
                        <Unlock className="w-3 h-3 inline mr-1" />
                        {language === 'pl' ? 'Bez hasła' : 'No password'}
                      </div>
                    </div>
                    <div className="flex gap-2 justify-center">
                      <Button variant="outline" size="sm" onClick={handleDownloadClientQR}>
                        <Download className="w-4 h-4 mr-2" />
                        {language === 'pl' ? 'Pobierz QR' : 'Download QR'}
                      </Button>
                      <Button variant="outline" size="sm" onClick={handleRevokeClientToken} className="text-destructive">
                        <RefreshCw className="w-4 h-4 mr-2" />
                        {language === 'pl' ? 'Unieważnij' : 'Revoke'}
                      </Button>
                    </div>
                  </div>

                  {/* Client Link */}
                  <div>
                    <label className="text-sm text-muted-foreground flex items-center gap-2 mb-1">
                      <Link className="w-4 h-4" />
                      {language === 'pl' ? 'Link dla klienta (bez hasła)' : 'Client link (no password)'}
                    </label>
                    <div className="flex gap-2">
                      <Input
                        value={clientQr.token_url}
                        readOnly
                        className="text-sm"
                      />
                      <Button
                        variant="outline"
                        onClick={() => handleCopy(clientQr.token_url, 'client')}
                      >
                        {copied === 'client' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-center py-8">
                  <QrCode className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground mb-4">
                    {language === 'pl'
                      ? 'Wygeneruj specjalny QR kod dla klienta, który pozwoli mu wejść bez hasła.'
                      : 'Generate a special QR code for the client that will allow them to enter without a password.'}
                  </p>
                  <Button onClick={handleGenerateClientQr} disabled={generatingClientQr}>
                    {generatingClientQr ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <QrCode className="w-4 h-4 mr-2" />
                    )}
                    {language === 'pl' ? 'Generuj QR dla klienta' : 'Generate client QR'}
                  </Button>
                </div>
              )}
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
